import { useState } from "react";
import { FileText } from "lucide-react";
import { upsertPrimeCompany, type PrimeCompany } from "../api/CrmApi";
import Modal from "shared/Modal";
import Button from "shared/Button";
import Alert from "shared/Alert";
import { FORM_CONTROL } from "shared/tableStyles";

interface Props {
  companyId: number;
  notes: string;
  onClose: () => void;
  onSaved: (company: PrimeCompany) => void;
}

/** Full raw-text editor for a company's notes — the escape hatch for
 * correcting a typo or rewording an old entry. Ordinary additions go
 * through CompanyNotesSection's own Add Note input instead; this modal is
 * only for editing what's already there. */
export default function EditCompanyNotesModal({ companyId, notes, onClose, onSaved }: Props) {
  const [text, setText] = useState(notes);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      // Only `notes` is sent — this never touches naics_codes, so it can't
      // trip the "at least one NAICS code" rule that only applies when a
      // caller actually touches naics_codes (see upsert_prime_company).
      const updated = await upsertPrimeCompany({ company_id: companyId, notes: text.trim() });
      onSaved(updated);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || "Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      size="md"
      title="Edit Notes (raw text)"
      icon={<FileText />}
      onClick={saving ? undefined : onClose}
      closeButtonCLick={saving ? undefined : onClose}
      footer={
        <>
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={saving}
          rows={10}
          className={`w-full px-3 py-2 ${FORM_CONTROL} resize-none disabled:opacity-60`}
        />
        <p className="text-[11px] text-muted-foreground">
          One note per line. This replaces the entire notes text with exactly what's here — use it to correct or
          reorder existing entries, not to add a new one (use the Add Note input for that).
        </p>
      </div>

      {error && (
        <Alert tone="danger" className="mt-4">{error}</Alert>
      )}
    </Modal>
  );
}
